import React from 'react';
import { connect } from 'react-redux';
import { createLoadingSelector } from '@hobs/utils/loading';
import PropTypes from 'prop-types';
import { H1 } from '@hobs/components/Headings';
import ObjectPath from 'object-path';
import ShortId from 'shortid';
import { Formik, Form, Field } from 'formik';
import { withTranslation } from 'react-i18next/src/index';
import Input from '@hobs/components/Input';
import Dropdown from '@hobs/components/Dropdown';
import DropZone from '@hobs/components/DropZone';
import Toast from '@hobs/components/Toast';
import { RaisedButton } from '@hobs/components/Buttons';
import {
  uploadAttachments,
  setAttachments,
  deleteAttachment,
  clearAttachments
} from '@hobs/actions/attachment';
import ApprovalSummary from 'sending-nation/src/containers/ApprovalSummary';
import {
  getSorFinanceData,
  updateSorFinanceData,
  getSorFinanceDataForSubmit,
  clearSorFinanceData
} from '../../../actions/sor';
import Activities from '../../Activities/Activities';
import Styles from './SorApprovals.module.scss';

class SorApprovals extends React.PureComponent {
  state = {
    sorFinance: null,
    isUploadingAttachment: false
  };

  async componentDidMount() {
    const { disabled } = this.props;
    if (!disabled) {
      await this.props.getSorFinanceDataForSubmit();
    }
    await this.props.getSorFinanceData(this.props.sorId).then(response => {
      this.props.setAttachments(response.data.attachments);
    });
  }

  componentWillUnmount() {
    this.props.clearSorFinanceData();
  }

  constructor(props) {
    super(props);
  }

  handleSubmit = (values, { setSubmitting, setErrors }) => {
    const { t, updateSorFinanceData } = this.props;
    const body = {
      ...values,
      attachments: [...this.props.attachments]
    };
    updateSorFinanceData(body).then(response => {
      if (response.success) {
        Toast.success(t('Sending Nation information updated successfully'));
      } else if (response.payload && response.payload.details) {
        const errors = response.payload.details.reduce((results, error) => {
          return {
            ...results,
            [error.target]: error.message[0]
          };
        }, {});

        setErrors(errors);
      } else if (response.payload) {
        Toast.warning(response.payload.message);
      } else {
        Toast.warning(t('Cannot update Sending Nation information'));
      }

      setSubmitting(false);
    });
  };

  handleFileUpload = value => {
    const { t } = this.props;
    this.setState({ isUploadingAttachment: true });

    if (value) {
      this.props.uploadAttachments({ attachments: value }).then(response => {
        if (response.success === true) {
          Toast.success(
            t(
              'File successfully added. Please click save to commit this change'
            )
          );
        } else if (response.payload) {
          Toast.warning(response.payload.message);
        } else {
          Toast.warning(t('Cannot add file'));
        }
        this.setState({ isUploadingAttachment: false });
      });
    }
  };

  handleFileRemove = value => {
    const { t } = this.props;
    this.props.deleteAttachment(value);
    Toast.success(
      t('File removed successfully. Please click save to commit this change')
    );
  };

  getUsersWhoCanConfirmSorList = () => {
    const { t } = this.props;
    const sorUserList = this.props.whoCanConfirmFsor.usersWhoCanConfirmFsor;
    if (sorUserList && sorUserList.length > 0) {
      const placeholder = (
        <option key={ShortId.generate()} value="">
          {t('Select User')}
        </option>
      );

      const options = sorUserList.map(user => {
        return (
          <option key={ShortId.generate()} value={user.userId}>
            {user.name}
          </option>
        );
      });

      return [placeholder, options];
    }
    return (
      <option key={ShortId.generate()} value="">
        {this.props.t('There are no users for this SN')}
      </option>
    );
  };

  getUserWhoCanConfirmSor = () => {
    const sorUser = ObjectPath.get(this.props.finance, 'user');
    if (sorUser) {
      return sorUser.userId;
    }
  };

  canRenderForm = () => {
    return Object.keys(this.props.finance).length > 0;
  };

  render() {
    const { finance, t, disabled, location, match, history } = this.props;
    const initialFormValues = {
      whoCanConfirmFsorId: this.getUserWhoCanConfirmSor(),
      whoCanConfirmFsorUser: finance.user ? finance.user.username : ''
    };

    return (
      <div className={Styles['wrapper']}>
        <ApprovalSummary></ApprovalSummary>
        <hr className={Styles['divider']}></hr>
        <div className={Styles['top-wrapper']}>
          <div className={Styles['left']}>
            <H1>{t('Sending Nation information')}</H1>
          </div>
        </div>
        <div className={Styles['middle-wrapper']}>
          {!this.props.isLoading && (
            <Formik
              validateOnBlur={false}
              validateOnChange={false}
              initialValues={initialFormValues}
              onSubmit={this.handleSubmit}
            >
              {({ isSubmitting, setFieldValue }) => (
                <Form>
                  <div className={Styles['input-group-wrapper']}>
                    <div className={Styles['input-group']}>
                      <span className={Styles['input-group-title']}>
                        {t('SOR confirmation responsible:')}
                      </span>
                      {!disabled && (
                        <Field
                          name="whoCanConfirmFsorId"
                          component={Dropdown}
                          label={t('Selected User')}
                          disabled={disabled}
                          onChange={e => {
                            const person = this.props.whoCanConfirmFsor.usersWhoCanConfirmFsor.find(
                              p => p.userId === e.target.value
                            );
                            if (person) {
                              setFieldValue(
                                'financePocPhone',
                                person.phone || ''
                              );
                            } else {
                              setFieldValue('financePocPhone', '');
                            }
                          }}
                        >
                          {this.getUsersWhoCanConfirmSorList()}
                        </Field>
                      )}
                      {disabled && (
                        <Field
                          name="whoCanConfirmFsorUser"
                          component={Input}
                          disabled={disabled}
                        >
                          {t('Selected User')}
                        </Field>
                      )}
                      <div className={Styles['uploads-group']}>
                        <span className={Styles['input-group-title']}>
                          {t('Attachments:')}
                        </span>
                        <DropZone
                          value={this.props.attachments}
                          onSelect={files => {
                            this.handleFileUpload(files);
                          }}
                          onRemove={file => {
                            this.handleFileRemove(file);
                          }}
                          disabled={disabled}
                        />
                      </div>
                    </div>
                  </div>
                  {!disabled && (
                    <div className={Styles['bottom-wrapper']}>
                      <RaisedButton
                        type="submit"
                        disabled={
                          isSubmitting || this.state.isUploadingAttachment
                        }
                      >
                        {t('Save')}
                      </RaisedButton>
                    </div>
                  )}
                </Form>
              )}
            </Formik>
          )}
          <hr className={Styles['divider']}></hr>
        </div>
        <section>
          <div>
            {
              <Activities
                location={location}
                match={match}
                history={history}
                disabled={disabled}
                sorId={this.props.sorId}
              />
            }
          </div>
        </section>
      </div>
    );
  }
}

SorApprovals.propTypes = {
  sorId: PropTypes.string,
  location: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  sorFinance: PropTypes.object,
  getSorFinanceData: PropTypes.func,
  finance: PropTypes.object,
  attachments: PropTypes.array,
  whoCanConfirmFsor: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  updateSorFinanceData: PropTypes.func,
  getSorFinanceDataForSubmit: PropTypes.func,
  t: PropTypes.func,
  clearSorFinanceData: PropTypes.func,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  uploadAttachments: PropTypes.func,
  setAttachments: PropTypes.func,
  deleteAttachment: PropTypes.func
};

const financeDataLoading = createLoadingSelector([
  'StatementOfRequirements/GetFinanceData'
]);

const mapStateToProps = state => ({
  finance: state.sor.finance,
  whoCanConfirmFsor: state.sor.whoCanConfirmFsor,
  isLoading: financeDataLoading(state),
  attachments: state.attachment.attachments
});

const mapDispatchToProps = {
  getSorFinanceData,
  updateSorFinanceData,
  getSorFinanceDataForSubmit,
  clearSorFinanceData,
  uploadAttachments,
  setAttachments,
  deleteAttachment,
  clearAttachments
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(SorApprovals));
